import { RowProps } from "antd/lib/row";
import Table, { ColumnProps, TableProps } from "antd/lib/table";
import ElementQueries, { ResizeSensor } from "css-element-queries";
import * as React from "react";
import { SortableContainer, SortableContainerProps, SortableElement, SortEnd } from "react-sortable-hoc";

import { Guid } from "guid-typescript";
import { IBaseProps } from "lib/data/interfaces";
import { OmitKeys } from "lib/types/OmitKeys";
import { clone } from "lodash";
import { curry, __ } from "lodash/fp";
import "./SortableTable.less";

interface ISortableTableProps<T> extends IBaseProps, TableProps<T>, OmitKeys<SortableContainerProps, ["lockAxis", "helperClass"]> {
	sortDisabled?: boolean;
	onSortEndDiff?: (oldRecords: T[], newRecords: T[]) => void;
	tableWidthSwitch?: number;
	mobileColumnCount?: number;
}

interface ISortableTableState {
	tableWidth: number | null;
	tableId: Guid;
}

const DraggableRowDefinition = (props: RowProps & { index: number, disabled?: boolean }) => {

	return (
		<tr {...props} />
	);
};

const DraggableRow = SortableElement(DraggableRowDefinition);

const getSortableContainerIndex = (_record: any, index: number, disabled?: boolean) => ({ index, disabled });

const SortableList = SortableContainer((props: ISortableTableProps<any>) => (
	<Table
		components={{
			body: {
				row: DraggableRow
			}
		}}
		onRow={
			curry(getSortableContainerIndex)(__, __, props.sortDisabled)
		}
		{...props}
	/>
));

export class SortableTable<T> extends React.Component<ISortableTableProps<T>, ISortableTableState> {

	public static defaultProps = {
		tableWidthSwitch: 500,
		mobileColumnCount: 2
	};

	// detects width changes on table
	private tableSensor?: ResizeSensor;

	public constructor(props: ISortableTableProps<T>) {

		super(props);

		this.state = {
			tableWidth: null,
			tableId: Guid.create()
		};
	}


	public componentDidMount() {

		const tableElement = document.querySelector(`#${this.getTableId()}`);

		// Register table sensor (to detect width changes)
		if (tableElement !== null) {

			this.tableSensor = new ElementQueries.ResizeSensor(tableElement, () => {

				this.setState({
					tableWidth: tableElement.clientWidth
				});
			});
		}
	}

	public componentWillUnmount() {

		// detach table sensor
		if (this.tableSensor !== undefined) {

			this.tableSensor.detach();
		}
	}

	public render() {

		const className = `sortable-table${this.props.className ? ` ${this.props.className}` : ""}`;

		return (
			<section id={this.getTableId()}>
				<SortableList
					{...this.props}
					columns={this.getTableColumns()}
					expandedRowRender={this.state.tableWidth !== null && this.state.tableWidth < this.props.tableWidthSwitch! ? this.getExpandedRow : undefined}
					className={className}
					helperClass="sortable-table-dragged-row"
					lockAxis="y"
					onSortEnd={this.onEntryRecordSort}
				/>
			</section>
		);
	}

	/**
	 * Gets the unique identifier for the table (primarily used for attaching the sensor)
	 */
	private getTableId = () => {
		return `sortable-table-${this.state.tableId.toString()}`;
	}

	/**
	 * Depending on the width of the table, will grab sub group of rows or all rows
	 */
	private getTableColumns = (): Array<ColumnProps<T>> | undefined => {

		// if we don't know the table width or the table width is greater than switch value, render ALL columns provided
		if (this.state.tableWidth !== null && this.state.tableWidth > this.props.tableWidthSwitch!) {

			return this.props.columns;
		}
		// render a subset of columns for mobile view
		else if (this.props.columns !== undefined) {

			const columns: Array<ColumnProps<T>> = [];

			this.props.columns.forEach((col, index) => {

				if (index <= this.props.mobileColumnCount! - 1) {

					columns.push(col);
				}
			});

			return columns;
		}

		return this.props.columns;
	}

	/**
	 * Depending on the width of the table, will return an expandable row
	 */
	private getExpandedRow = (record: T): React.ReactNode | undefined => {

		if (this.props.columns !== undefined) {

			return (
				this.props.columns.map((column, index) => {

					let valueToRender: React.ReactNode;

					// Find the value to render
					if (column.dataIndex !== undefined && record[column.dataIndex as keyof T] !== undefined) {

						valueToRender = record[column.dataIndex as keyof T];
					}
					else if (column.render !== undefined) {

						valueToRender = (column.render as (record: T) => React.ReactNode)(record);
					}

					// Render title + value
					return (
						<div
							key={`${column.title}-${index}`}
							className="expanded-row-data-section"
						>
							<div className="expanded-row-title">
								{
									column.title !== undefined
									?
									column.title
									:
									null
								}:
							</div>
							<div className="expanded-row-content">{valueToRender}</div>
						</div>
					);
				})
			);
		}

		return "No data to display";
	}

	private onEntryRecordSort = (sortHandler: SortEnd) => {

		const { oldIndex, newIndex } = sortHandler;
		const orderedEntries = clone(this.props.dataSource);

		if (this.props.dataSource !== undefined && orderedEntries !== undefined) {

			const recordToBeSorted = orderedEntries[oldIndex];

			// "Move" the record from the old index, to the new index
			orderedEntries.splice(oldIndex, 1);
			orderedEntries.splice(newIndex, 0, recordToBeSorted);

			if (this.props.onSortEndDiff !== undefined) {

				this.props.onSortEndDiff(this.props.dataSource, orderedEntries);
			}
		}
	}
}
